import update from 'immutability-helper';
import { combineReducers } from 'redux';
import isEqual from 'lodash/isEqual';
import castArray from 'lodash/castArray';
import { DATE_SHORTCUTS } from '@utils/constants';
import { INVERTED_METRIC_SWITCH_MAP, METRIC_SWITCH_MAP } from '@utils/metrics';
import * as drilldownTypes from '@store/summaryContainer/drilldown/types';
import * as compareDrawerTypes from '@store/compareDrawer/types';
import * as metricChartContainerTypes from '@store/metricsChartContainer/types';
import { calcShortcutInterval } from '@utils';
import * as types from './types';
import { reducer as sharing } from './sharing';
import { reducer as config } from './config';
import { COMPARE_SHARING_SCOPE } from './sharing/types';

/* Set default time interval for compare dashboard store */
const [from, to] = calcShortcutInterval(DATE_SHORTCUTS.last6Hours);
const timeInterval = { from: from.format(), to: to.format(), shortcut: DATE_SHORTCUTS.last6Hours };

const initialZoomInterval = {
  from: null, // datetime
  to: null, // datetime
  start: 0,
  end: 100,
};

const loaderSuccess = { isLoading: false, showError: false };

export const initialState = {
  dataIndex: -1,
  filters: [],
  interval: timeInterval,
  metricList: [],
  savedFilters: [], // Used when removing saved filter in compareDrawer
  newDashboard: false,
  loader: {
    isLoading: false,
    showError: false,
    dismissable: true,
    errorMessage: null,
  },
  activeEditId: null,
  zoomInterval: initialZoomInterval,
  compareData: {
    dataObj: {},
    colors: [],
    filterNames: [],
    totalObj: {},
    metrics: [],
    filterIndices: [],
  },
  compareZoomData: null,
  requests: [],
  exportedMetric: '',
};

const comparison = (state = initialState, action) => {
  switch (action.type) {
    case drilldownTypes.SELECT_COMPARE_MODE: {
      return update(state, {
        metricList: { $set: [] },
        savedFilters: { $set: [] },
        activeEditId: { $set: null },
      });
    }
    case types.TOGGLE_FILTER_INDEX: {
      const { filterIndices } = state.compareData;
      const indices = castArray(filterIndices);
      const indexOf = indices.indexOf(action.index);
      if (indexOf !== -1) {
        indices.splice(indexOf, 1);
      } else {
        indices.push(action.index);
      }
      const updatedIndices = [...indices];
      return update(state, {
        compareData: {
          $merge: {
            filterIndices: updatedIndices,
          },
        },
      });
    }

    case types.CLEAR_FILTER_INDICES: {
      return update(state, {
        compareData: {
          $merge: {
            filterIndices: [],
          },
        },
      });
    }
    case types.SET_FILTER_INDICES: {
      return update(state, {
        compareData: {
          $merge: {
            filterIndices: action.indices,
          },
        },
      });
    }
    case types.ADD_METRIC: {
      return update(state, {
        metricList: (list) => {
          const metrics = castArray(action.metric);
          metrics.forEach((metric) => {
            const metricName = INVERTED_METRIC_SWITCH_MAP[metric] || metric;
            const switchMetric = METRIC_SWITCH_MAP[metricName];
            if (!list.includes(metricName)) {
              list.push(metricName);
            }
            if (list.includes(metricName) && switchMetric !== undefined) {
              list.pop(metricName);
              list.push(switchMetric);
            }
          });
          return [...list];
        },
      });
    }
    case types.REMOVE_METRIC: {
      return update(state, {
        metricList: (list) => {
          const metrics = castArray(action.metric);
          metrics.forEach((metric) => {
            if (list.includes(metric)) {
              const index = list.indexOf(metric);
              list.splice(index, 1);
            }
          });
          return [...list];
        },
      });
    }
    /* TODO: needs to be removed if no side effects */
    case types.ADD_SAVED_FILTER: {
      const { filters, name } = action;

      const hasDuplicate = !!state.savedFilters.find((item) => isEqual(item.filters, filters));
      if (!hasDuplicate) {
        return { ...state, savedFilters: [...state.savedFilters, { name, filters }] };
      }
      return state;
    }

    case types.EMPTY_SAVED_FILTER: {
      return { ...state, savedFilters: [] };
    }
    case types.TOGGLE_NEW_COMPARISON_DASHBOARD: {
      return { ...state, newDashboard: action.flag };
    }
    case compareDrawerTypes.EXIT_COMPARE_MODE:
      return {
        ...state,
        metricList: [],
        savedFilters: [],
      };
    case metricChartContainerTypes.SELECTED_METRIC_CHART: {
      const { metricName } = action;
      return update(state, {
        metricList: {
          $set: [METRIC_SWITCH_MAP[metricName] || metricName],
        },
      });
    }

    case types.SET_LOADER: {
      return update(state, {
        $merge: {
          loader: action.loader,
        },
      });
    }
    case types.FETCH_COMPARE_DATA_SUCCESS: {
      return update(state, {
        compareData: {
          $set: action.data,
        },
        compareZoomData: { $set: null },
        zoomInterval: { $set: initialZoomInterval },
        loader: { $set: loaderSuccess },
      });
    }
    case types.UPDATE_COMPARE_DATA: {
      return update(state, {
        compareData: {
          $set: action.data,
        },
      });
    }
    case types.FETCH_ZOOM_DATA_SUCCESS: {
      return update(state, {
        compareZoomData: {
          $set: action.data,
        },
        loader: { $set: loaderSuccess },
      });
    }
    case types.UPDATE_COMPARE_ZOOM_DATA: {
      return update(state, {
        compareZoomData: {
          $set: action.data,
        },
      });
    }
    case types.CLEAR_COMPARE_ZOOM_DATA: {
      return update(state, {
        compareZoomData: {
          $set: null,
        },
      });
    }

    case types.APPLY_SAVED_FILTERS:
      return update(state, {
        savedFilters: {
          $set: action.filters,
        },
      });
    case types.APPLY_SAVED_METRICLIST:
      return update(state, {
        metricList: {
          $set: action.list,
        },
      });
    case types.APPLY_SAVED_INDEX:
      return update(state, {
        compareData: {
          $merge: {
            filterIndices: action.indices,
          },
        },
      });
    case types.MODIFY_COMPARISON: {
      const { metricList, savedFilters } = action.config;
      return update(state, {
        activeEditId: { $set: action.activeEditId },
        metricList: { $set: metricList },
        savedFilters: { $set: savedFilters },
      });
    }
    case types.SET_ACTIVE_EDIT_ID: {
      return update(state, {
        activeEditId: { $set: action.activeEditId },
      });
    }

    case types.UPDATE_ZOOM_INTERVAL: {
      return update(state, {
        zoomInterval: { $set: action.zoomInterval },
      });
    }
    case types.UPDATE_ZOOM_TOTAL: {
      return update(state, {
        compareData: {
          $merge: {
            totalObj: action.totalObj,
          },
        },
        loader: { $set: loaderSuccess },
      });
    }
    case types.SAVE_COMPARE_QUERIES: {
      return update(state, {
        requests: {
          $set: action.queries,
        },
      });
    }
    case types.SAVE_EXPORTED_METRIC: {
      return update(state, {
        exportedMetric: {
          $set: action.exportedMetric,
        },
      });
    }
    default:
      return state;
  }
};

export default combineReducers({
  comparison,
  config,
  [COMPARE_SHARING_SCOPE]: sharing,
});
